// @givmo/employer-module — the HOST seam of the sign-up funnel (EP-MNT8).
//
// ══ WHY THE FUNNEL ASKS ITS HOST FOR FIVE THINGS ════════════════════════════════════════════════
//
// The company sign-up funnel (EP-SUITE2: the chooser, the details and the company create, the email
// and password, the work-email code, the waiting room and request-access) is ONE implementation that
// two hosts render: the employer portal (`apps/admin`, until EP-MNT12 forwards it) and the company
// console (`apps/company`, the company's sign-up door from EP-MNT8). The screens hold the behaviour —
// NF-FIX21's verify-before-create, the EP-SUITE2 join pivot, every FIX's exits — and ask the host for
// what only the host has:
//   - `auth`  — its signed-in account and the funnel's actions, from the host's own auth chokepoint
//               (one chokepoint per property, AS-SUITE4: a package never subscribes to Firebase);
//   - `paths` — its address book (`auth-routing.ts`): where its doors are, where a company lands;
//   - `look`  — the register its doors are drawn in (`sign-up-look.ts`);
//   - `words` — the three words that name the host itself: its sign-in, its sign-out, its app;
//   - `Shell` — the frame its doors wear (the split brand panel and the card).
//
// There is no default host. A package cannot reach an app's auth, so a screen rendered without a
// provider throws by name rather than drawing a funnel that cannot sign anyone up.

import { createContext, use, type ComponentType, type ReactNode } from 'react';
import type { User, UserCredential } from 'firebase/auth';
import type {
  AdminProviderId,
  AdminSignUpResult,
  AuthRoutingState,
  CompanyRegistration,
  CompanyRegistrationResult,
  OnboardingResolution,
} from '@givmo/auth';
import type { SignUpPaths } from './auth-routing';
import type { SignUpLook, SignUpWords } from './sign-up-look';

/** The email sign-up's fields (the details screen's names ride along to the account's display name). */
export interface SignUpWithEmailParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/**
 * What the funnel needs from the host's auth context — each a thin wrapper over `@givmo/auth`'s ADMIN
 * flows that the host's chokepoint runs (its suite-session ordering included: the sign-up mark, one
 * establish after provisioning). The portal's `AuthState` satisfies it as it stands.
 */
export interface SignUpAuth {
  /** The signed-in Firebase account, or `null`. */
  user: User | null;
  /** The host's ONE sign-out (it releases the suite session first). */
  signOut: () => Promise<void>;
  /** Provider sign-UP: a new account; an existing one fails "already registered" (signed back out). */
  signUpWithProvider: (id: AdminProviderId) => Promise<UserCredential>;
  /** Email sign-up: the account and its Givmo row, and NO company (NF-FIX21). */
  signUpWithEmail: (params: SignUpWithEmailParams) => Promise<AdminSignUpResult>;
  /** Register the company for the signed-in account (the join pivot surfaces here too). */
  completeProviderRegistration: (
    registration: CompanyRegistration,
  ) => Promise<CompanyRegistrationResult>;
  /** EP-SUITE2's onboarding resolve for a verified work email (join-or-create on only). */
  resolveDomain: () => Promise<OnboardingResolution>;
  /** Send the work-email code (bootstrapping the Givmo row first where none exists). */
  sendOtp: () => Promise<void>;
  /** Confirm the code; the account then reads as verified. Throws on a wrong code. */
  confirmOtp: (code: string) => Promise<void>;
  /** Re-resolve where the signed-in account belongs (company + verified). */
  refreshRoutingState: (opts?: {
    isProviderRegistration?: boolean;
  }) => Promise<AuthRoutingState | null>;
  /** The suite-session establish for a row the join-request intake provisioned (AS-SUITE10). */
  establishAfterProvisioning: (user: User) => void;
}

/** What a host's auth frame takes. */
export interface SignUpShellProps {
  children: ReactNode;
  /** The screen's root test id. */
  testId: string;
  /** A wider card for the multi-field screens. */
  wide?: boolean;
}

/** Everything the funnel takes from the host that renders it. */
export interface SignUpHost {
  auth: SignUpAuth;
  paths: SignUpPaths;
  look: SignUpLook;
  words: SignUpWords;
  Shell: ComponentType<SignUpShellProps>;
}

const SignUpHostContext = createContext<SignUpHost | null>(null);

/** Bind the funnel to this host. Both hosts render it around their `/sign-up` routes. */
export function SignUpHostProvider({ host, children }: { host: SignUpHost; children: ReactNode }) {
  return <SignUpHostContext value={host}>{children}</SignUpHostContext>;
}

/** The host the funnel renders in. Throws outside a {@link SignUpHostProvider}. */
export function useSignUpHost(): SignUpHost {
  const host = use(SignUpHostContext);
  if (host === null) {
    throw new Error('A sign-up screen must be rendered inside a <SignUpHostProvider>.');
  }
  return host;
}

/**
 * A screen's heading and its explanation, held tight together as both hosts' sign-in doors hold
 * their own.
 */
export function SignUpHeading({
  heading,
  children,
}: {
  /** The heading element, already styled by the caller's look. */
  heading: ReactNode;
  /** The explanation under it, if the screen has one. */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      {heading}
      {children}
    </div>
  );
}
